// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { usePermissionChecker } from '@/authorization/hooks/use-user-policy';
import { Permission } from '@/authorization/types';
import { Download as DownloadIcon } from '@/constants/hardfin-standard-icons';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback } from 'react';

const BASE_HEADERS = [
  'Date',
  'Duration',
  'Category',
  'Worker',
  'Description',
  'Project',
];

const RATE_HEADERS = ['Hourly Rate', 'Currency (Ex USD)'];

interface TemplateDownloadProps {
  onBack: () => void;
  onNext: () => void;
}

export function TemplateDownload(props: TemplateDownloadProps) {
  // The importer rejects the rate columns outright for users without time.rate,
  // so the template must not hand them a file that can never be uploaded
  const { hasPermission } = usePermissionChecker();
  const ratePermission = hasPermission('time.rate', 'create');
  const canSetRate =
    ratePermission === Permission.PermissionExplicitTrue ||
    ratePermission === Permission.PermissionImplicitTrue;

  const handleDownloadTemplate = useCallback(() => {
    const headers = canSetRate
      ? [...BASE_HEADERS, ...RATE_HEADERS]
      : BASE_HEADERS;

    const csvContent = headers.join(',') + '\n';

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `time_data_template_${DateTime.now().toFormat('yyyy-MM-dd_HHmm')}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [canSetRate]);

  return (
    <Card variant={'outlined'}>
      <CardContent>
        <Box
          sx={{
            px: 3,
            py: 2,
            backgroundColor: (theme) => theme.palette.grey.A100,
            borderRadius: '10px',
            marginBottom: '10px',
          }}
        >
          <Typography variant="subtitle1" gutterBottom>
            Template for time data
          </Typography>
          <Typography variant="subtitle2" gutterBottom>
            Required fields
          </Typography>
          <ul style={{ paddingLeft: '24px', marginTop: '8px' }}>
            <li>
              <Typography variant="body2">
                Date - date (YYYY-MM-DD format)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Duration - time (1.5h, 90m, 1h 30m, or 1:30)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">Category - text</Typography>
            </li>
            <li>
              <Typography variant="body2">
                Worker - email address or person's name (must be unique in your
                organization)
              </Typography>
            </li>
          </ul>
          <Typography variant="subtitle2" gutterBottom>
            Optional fields
          </Typography>
          <ul style={{ paddingLeft: '24px', marginTop: 0, marginBottom: 0 }}>
            <li>
              <Typography variant="body2">Description - text</Typography>
            </li>
            <li>
              <Typography variant="body2">Project - project name</Typography>
            </li>
            {canSetRate && (
              <>
                <li>
                  <Typography variant="body2">
                    Hourly Rate - number (ex: 75.00)
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Currency - ISO code (ex: USD); defaults to your organization
                    currency if blank
                  </Typography>
                </li>
              </>
            )}
          </ul>
        </Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Important information about the template</AlertTitle>
          <ul style={{ paddingLeft: '24px', marginTop: '8px' }}>
            <li>
              Date format must be YYYY-MM-DD (ex: '2020-06-25' for June 25,
              2020)
            </li>
            <li>
              Duration can be in various formats: '1.5h' (1.5 hours), '90m' (90
              minutes), '1h 30m' (1 hour 30 minutes), or '1:30' (1 hour 30
              minutes)
            </li>
            <li>
              Worker must match an existing name or email address from your
              organization
            </li>
            <li>Project must match an existing project name</li>
            <li>
              Hourly rate should be in currency format (ex: 75.00); more than
              two decimal places is rejected, while fewer (ex: 10.1) is padded
              to 10.10
            </li>
            <li>Optional fields do not need to be included</li>
          </ul>
        </Alert>

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Button variant="outlined" onClick={props.onBack}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            Download template
          </Button>
          <Button variant="contained" onClick={props.onNext}>
            Next
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
